import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import uPlot from "uplot";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { IS_ADMIN_HOST } from "@/lib/auth";
import { dashboardSeverity } from "@/lib/pqTuning";
import { useEventQueue } from "@/stores/eventQueue";
import { SparklineUplot } from "@/components/SparklineUplot";

// PqActivity — window-wide PQ event scatter, one mini-chart per system.
//
// Per system: a uPlot scatter where x=time across the window's lifetime,
// y=peak current (max across phases), dots colored by severity, with a
// horizontal reference line at the operator-set nominal_current_a (or the
// auto-computed median pre-event RMS as a fallback). Click a dot → open
// the EventDetail view. Live: refetches whenever a pq_event_keep arrives
// matching this window — current implementation is poll-on-WS-arrival
// rather than incremental append (simpler and the dataset is small).

interface PqEventSummary {
  event_id: string;
  trigger_ts: string;
  event_type: string | null;
  severity: string | null;
  duration_ms: number | null;
  depth_pct: number | null;
  magnitude_pct: number | null;
  peak_a: number | null;
  peak_phase: string | null;
}

interface SystemSummary {
  system_id: string;
  service_name: string | null;
  nominal_current_a: number | null;
  fallback_nominal_a: number | null;
  // True event count for the window. `events` is server-downsampled (worst
  // event per time bucket, capped per system) so the scatter stays fast and
  // legible at any volume — read total_count for the count, not events.length.
  total_count: number;
  events: PqEventSummary[];
}

interface SummaryResponse {
  window_id: string;
  window_name: string;
  window_started_at: string;
  window_ended_at: string | null;
  now: string;
  systems: SystemSummary[];
}

const SEVERITY_COLORS: Record<string, string> = {
  minor:    "#facc15", // yellow-400
  moderate: "#fb923c", // orange-400
  severe:   "#ef4444", // red-500
};
const UNCATEGORIZED_COLOR = "#94a3b8"; // slate-400 — events without a severity classification

const SEVERITY_ORDER = ["minor", "moderate", "severe", "uncategorized"] as const;
type SeverityKey = typeof SEVERITY_ORDER[number];

function SystemChart({
  sys,
  windowStartTs,
  windowEndTs,
  syncRange,
  onSyncRangeChange,
  onEditNominal,
  onClickEvent,
}: {
  sys: SystemSummary;
  windowStartTs: number;  // epoch seconds
  windowEndTs: number;    // epoch seconds
  syncRange: [number, number] | null;
  onSyncRangeChange: (range: [number, number]) => void;
  onEditNominal: (system_id: string, value: number) => void;
  onClickEvent: (event_id: string) => void;
}) {
  // Click handler needs the event_id at the hovered index — events shift on
  // every refetch, but a ref keeps the closure pointed at the current array.
  const eventsRef = useRef(sys.events);
  eventsRef.current = sys.events;

  const effectiveNominal = sys.nominal_current_a ?? sys.fallback_nominal_a;

  // Group events into severity series so uPlot can color each bucket
  // independently — uPlot's per-point color is awkward; per-series is easy.
  const seriesData = useMemo(() => {
    const xs = sys.events.map((e) => Date.parse(e.trigger_ts) / 1000);
    const buckets: Record<SeverityKey, (number | null)[]> = {
      minor:         [],
      moderate:      [],
      severe:        [],
      uncategorized: [],
    };
    for (const e of sys.events) {
      const sev: SeverityKey = dashboardSeverity(e.severity);
      for (const k of SEVERITY_ORDER) {
        buckets[k].push(k === sev ? e.peak_a : null);
      }
    }
    return [xs, ...SEVERITY_ORDER.map((k) => buckets[k])] as unknown as uPlot.AlignedData;
  }, [sys.events]);

  // Peak current across all events. Always reduced — never
  // `Math.max(...sys.events.map(...))`: spreading a large event array as
  // function arguments overflows the call stack (RangeError), and with no
  // error boundary that crash black-screens the whole PWA.
  const peakA = useMemo(
    () => sys.events.reduce((m, e) => Math.max(m, e.peak_a ?? 0), 0),
    [sys.events],
  );

  // Y-axis range: 0 to max(peak, nominal) × 1.2 so the dots at top still
  // have headroom and the nominal line is visible even if no events
  // have crossed it yet.
  const yRange = useMemo((): [number, number] => {
    const yTop = Math.max(peakA, effectiveNominal ?? 0) * 1.2 || 1;
    return [0, yTop];
  }, [peakA, effectiveNominal]);

  const series = useMemo<uPlot.Series[]>(() => [
    { label: "time" },
    ...SEVERITY_ORDER.map((k) => ({
      label: `peak A (${k})`,
      paths: () => null,
      points: {
        show: true,
        size: 9,
        stroke: SEVERITY_COLORS[k] ?? UNCATEGORIZED_COLOR,
        fill: SEVERITY_COLORS[k] ?? UNCATEGORIZED_COLOR,
      },
    } as uPlot.Series)),
  ], []);

  const axes = useMemo<uPlot.Axis[]>(() => [
    {
      stroke: "rgba(255,255,255,0.5)",
      grid: { stroke: "rgba(255,255,255,0.05)" },
      values: (_u, vs) =>
        vs.map((v) => {
          const d = new Date(v * 1000);
          return `${d.getHours().toString().padStart(2, "0")}:${d
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;
        }),
    },
    {
      stroke: "rgba(255,255,255,0.5)",
      grid: { stroke: "rgba(255,255,255,0.05)" },
      values: (_u, vs) => vs.map((v) => `${v.toFixed(0)}A`),
      size: 50,
    },
  ], []);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-baseline justify-between text-base">
          <span>
            {sys.service_name ?? sys.system_id}
            {sys.service_name && (
              <span className="ml-2 text-xs font-normal text-muted-foreground font-mono">
                {sys.system_id}
              </span>
            )}
          </span>
          <NominalEditor
            systemId={sys.system_id}
            value={sys.nominal_current_a}
            fallback={sys.fallback_nominal_a}
            onSave={onEditNominal}
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <SparklineUplot
          data={seriesData}
          series={series}
          xRange={[windowStartTs, windowEndTs]}
          yRange={yRange}
          height={110}
          nominal={effectiveNominal}
          syncRange={syncRange}
          onSyncRangeChange={onSyncRangeChange}
          onIndexClick={(idx) => {
            const ev = eventsRef.current[idx];
            if (ev) onClickEvent(ev.event_id);
          }}
          axes={axes}
        />
        <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>
            {sys.total_count} event{sys.total_count === 1 ? "" : "s"}
            {sys.events.length > 0 && (
              <>
                {" · "}peak {peakA.toFixed(1)}A
              </>
            )}
          </span>
          <span className="flex items-center gap-3">
            {SEVERITY_ORDER.filter((k) =>
              sys.events.some((e) => dashboardSeverity(e.severity) === k)
            ).map((k) => (
              <span key={k} className="flex items-center gap-1">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: SEVERITY_COLORS[k] ?? UNCATEGORIZED_COLOR }}
                />
                {k}
              </span>
            ))}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function NominalEditor({
  systemId,
  value,
  fallback,
  onSave,
}: {
  systemId: string;
  value: number | null;
  fallback: number | null;
  onSave: (system_id: string, value: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value != null ? String(value) : "");

  if (!IS_ADMIN_HOST) {
    // Client view — show the value (or fallback) as read-only label
    return (
      <span className="text-xs font-normal text-muted-foreground">
        nominal {value != null ? `${value}A` : fallback != null ? `~${fallback.toFixed(1)}A (auto)` : "—"}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value != null ? String(value) : "");
          setEditing(true);
        }}
        className="text-xs font-normal text-muted-foreground hover:text-foreground hover:underline"
        title="Click to set nominal current"
      >
        nominal {value != null ? `${value}A` : fallback != null ? `~${fallback.toFixed(1)}A (auto)` : "—"}
      </button>
    );
  }

  const submit = () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n <= 0) {
      setEditing(false);
      return;
    }
    onSave(systemId, n);
    setEditing(false);
  };

  return (
    <span className="flex items-center gap-1 text-xs font-normal">
      <input
        type="number"
        step="0.1"
        min="0"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") setEditing(false);
        }}
        autoFocus
        className="h-6 w-16 rounded border border-input bg-transparent px-1 text-xs"
      />
      <span className="text-muted-foreground">A</span>
      <Button size="sm" variant="ghost" className="h-6 px-2" onClick={submit}>
        ✓
      </Button>
    </span>
  );
}

export function PqActivity({ windowId }: { windowId: string }) {
  const [data, setData] = useState<SummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Shared zoom across every swimlane row — scrolling on one zooms them all
  // since they're all the same window's time axis.
  const [syncRange, setSyncRange] = useState<[number, number] | null>(null);
  const navigate = useNavigate();

  // Trigger refetch whenever a new pq_event arrives matching this window
  // (the eventQueue store is updated by the WS layer in useDynoWs).
  // events is a Map; size changes when new ones land.
  const liveCount = useEventQueue((s) => s.events.size);

  const refetch = () => {
    api
      .get<SummaryResponse>(
        `/api/pq/window/${encodeURIComponent(windowId)}/event-summary`
      )
      .then(setData)
      .catch((e) => setError(String(e)));
  };

  useEffect(() => {
    if (!windowId) return;
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowId, liveCount]);

  const handleEditNominal = (system_id: string, value: number) => {
    api
      .post(`/api/equipment/config/${encodeURIComponent(system_id)}`, {
        pq_nominal_current_a: String(value),
      })
      .then(() => refetch())
      .catch((e) => setError(String(e)));
  };

  const handleClickEvent = (event_id: string) => {
    navigate(`/events/${encodeURIComponent(event_id)}`);
  };

  // Window time range for shared X-axis. Use endedAt if window is closed,
  // otherwise "now" so live events appear at the right edge.
  const { startTs, endTs } = useMemo(() => {
    if (!data) return { startTs: 0, endTs: 0 };
    return {
      startTs: Date.parse(data.window_started_at) / 1000,
      endTs: data.window_ended_at
        ? Date.parse(data.window_ended_at) / 1000
        : Date.parse(data.now) / 1000,
    };
  }, [data]);

  if (error) {
    return <div className="text-sm text-destructive">PQ Activity: {error}</div>;
  }
  if (!data) {
    return <div className="text-sm text-muted-foreground">Loading PQ activity…</div>;
  }
  if (data.systems.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        No PQ events recorded in this window yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {data.systems.map((sys) => (
        <SystemChart
          key={sys.system_id}
          sys={sys}
          windowStartTs={startTs}
          windowEndTs={endTs}
          syncRange={syncRange}
          onSyncRangeChange={setSyncRange}
          onEditNominal={handleEditNominal}
          onClickEvent={handleClickEvent}
        />
      ))}
      <div className="text-[10px] text-muted-foreground italic px-1">
        Scroll = zoom · Shift+scroll = pan · double-click = reset · click a dot to open the event
      </div>
    </div>
  );
}
